/**
 * Which visual register the app is in.
 *
 * §9.3: kid and adult differ by token values and density, not by component.
 * Exactly one attribute carries it — `data-theme` on the document — so nothing
 * branches on the mode to decide how something *looks*.
 */
export type Register = 'kid' | 'adult';

/**
 * The register a character puts the app in.
 *
 * Inferred from `characters.audience` rather than asked at signup. The column
 * already exists, is already seeded for the whole cast and is already on the
 * wire, so an age band on the profile screen would add a migration, a question,
 * and a second source of truth that can disagree with the character actually
 * chosen. The character *is* the mode.
 *
 * Everything unrecognised — `both`, a value from a newer migration, no
 * character yet — is adult, because adult is the conservative register: it is
 * usable by a child, while the kid register is not really usable by an adult.
 *
 * That reasoning was written when the register decided only how the app
 * looks. The progress screen (design §3/§5) now also has it gate whether a
 * child is shown their own errors and a debrief — PRD §8.2 content, not
 * styling — and in that direction the adult fallback is not the safe one:
 * `audience = 'both'` is a real schema value, so one seed row could put a
 * child in the adult register and past its §8.2 gate. Mapping `both` to kid
 * for this decision is a product call not taken here.
 */
export function registerFor(audience: string | null | undefined): Register {
  return audience === 'kid' ? 'kid' : 'adult';
}
